import { baseApi } from 'shared/api/base-api';
import type {
  PlayerStatsResolverContext,
  ResolverRankedPlayer,
  ResolverTeam,
} from 'modules/manager/domain/placeholder';
import {
  playerPositionLabel,
  rankPlayers,
  type PlayerRankingSettings,
} from 'modules/manager/domain/player-stats';

export interface StatsUserDto {
  id: number;
  name: string;
  surname: string;
  middleName?: string | null;
  photo?: { externalUrl?: string } | null;
}

export interface ChampionshipStatsLineDto {
  /** Nullable in the schema — it is the same type as the `total` aggregate, which has no user. */
  user?: StatsUserDto | null;
  games: number;
  goals: number;
  assists: number;
  // Lowercase-glued in the schema, unlike TeamStats' camelCase spelling.
  yellowcards: number;
  redcards: number;
}

export interface StatsTeamDto {
  id: number;
  name: string;
  shortName: string;
  emblem?: { externalUrl?: string } | null;
}

export interface ChampionshipApplicationDto {
  id: number;
  state: string;
  number?: number | null;
  /** Free-form String in the schema; in practice a PlayerPosition value. */
  position?: string | null;
  user: { id: number };
  team?: StatsTeamDto | null;
}

export interface StatsChampionshipDto {
  id: number;
  name: string;
  images?: { externalUrl?: string }[] | null;
  tournament?: { id: number; name: string; images?: { externalUrl?: string }[] | null } | null;
  championshipApplications?: ChampionshipApplicationDto[] | null;
}

export interface PlayerStatsRenderDto {
  championshipStatsByPlayer?: { lines: ChampionshipStatsLineDto[] } | null;
  championship?: StatsChampionshipDto | null;
}

export interface PlayerStatsRenderArgs {
  championshipId: number;
  ranking: PlayerRankingSettings;
}

const ACCEPTED_STATE = 'ACCEPTED';

// Why one document with two root fields: `ChampionshipStatsLine` carries NO team
// and NO position (docs/schema.graphql), so shirt number, team and amplua must
// come from `championshipApplications`, joined on `user.id`. Both are root Query
// fields, so this is still exactly one HTTP request — the same "no fan-out"
// property standings-render-api.ts has.
//
// `total` is deliberately not selected: it is a `ChampionshipStatsLine` with no
// user that we would only have to filter back out.
//
// Photo comes from `user.photo.externalUrl` only. `tournamentAvatars` is NOT
// used (product decision: it is broken).
//
// `championshipStatsByPlayer` also takes an optional `teamId` — reserved for a
// future "top players of team X" render; unset means championship-wide.
const PLAYER_STATS_RENDER_QUERY = `#graphql
query getPlayerStatsRender($championshipId: Int!) {
  championshipStatsByPlayer(championshipId: $championshipId) {
    lines {
      user {
        id
        name
        surname
        middleName
        photo { externalUrl }
      }
      games
      goals
      assists
      yellowcards
      redcards
    }
  }
  championship(id: $championshipId) {
    id
    name
    images { externalUrl }
    tournament { id name images { externalUrl } }
    championshipApplications {
      id
      state
      number
      position
      user { id }
      team { id name shortName emblem { externalUrl } }
    }
  }
}
`;

export const statRenderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPlayerStatsRenderContext: build.query<PlayerStatsResolverContext, PlayerStatsRenderArgs>({
      async queryFn({ championshipId, ranking }, _api, _extra, baseQuery) {
        const res = await baseQuery({ query: PLAYER_STATS_RENDER_QUERY, variables: { championshipId } });
        if (res.error) return { error: res.error };
        const dto = res.data as PlayerStatsRenderDto | undefined;
        if (!dto?.championship) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Championship not found' } as never };
        }

        return { data: buildPlayerStatsRenderContext(dto, ranking) };
      },
      keepUnusedDataFor: 30,
    }),
  }),
  overrideExisting: false,
});

/**
 * Pure transform from the one-document response into the render context: shared
 * tournament/championship head, stats lines joined to accepted applications on
 * `user.id`, then ranked and sliced. Exported (not just used by `queryFn`) so
 * the join, the transfer/no-application edge cases and the ranking can be unit
 * tested without going through the network.
 */
export function buildPlayerStatsRenderContext(
  dto: PlayerStatsRenderDto,
  ranking: PlayerRankingSettings,
): PlayerStatsResolverContext {
  const c = dto.championship;
  const tournament = {
    id: c?.tournament?.id ?? 0,
    name: c?.tournament?.name ?? '',
    logoUrl: c?.tournament?.images?.[0]?.externalUrl,
  };
  const championship = {
    id: c?.id ?? 0,
    name: c?.name ?? '',
    logoUrl: c?.images?.[0]?.externalUrl,
  };

  const appByUserId = pickApplications(c?.championshipApplications);

  const players: ResolverRankedPlayer[] = [];
  for (const line of dto.championshipStatsByPlayer?.lines ?? []) {
    // `user` is nullable on ChampionshipStatsLine (it is the same type as the
    // `total` aggregate). A line with no user identifies nobody and cannot fill
    // a rank slot.
    if (!line.user) continue;

    const app = appByUserId.get(line.user.id);
    players.push({
      player: {
        id: line.user.id,
        name: formatPlayerName(line.user),
        nameParts: {
          firstName: line.user.name,
          lastName: line.user.surname,
          middleName: line.user.middleName ?? undefined,
        },
        number: app?.number ?? undefined,
        photoUrl: line.user.photo?.externalUrl,
      },
      team: app?.team ? toResolverTeam(app.team) : undefined,
      positionLabel: playerPositionLabel(app?.position),
      stats: {
        games: line.games,
        goals: line.goals,
        assists: line.assists,
        yellowCards: line.yellowcards,
        redCards: line.redcards,
      },
    });
  }

  return {
    mode: 'player_stats',
    tournament,
    championship,
    rankedPlayers: rankPlayers(players, ranking),
  };
}

/**
 * One application per user, keyed by `user.id`.
 *
 * `championshipStatsByPlayer` dedupes by user; `championshipApplications` does
 * not — a player transferred mid-championship has one ACCEPTED application per
 * team they played for, while their goals are aggregated into a single stats
 * line with no team attribution. There is therefore no per-goal-correct team,
 * and the product decision is to show the team the player was **first**
 * registered with in this championship: lowest `id` wins, since the column is a
 * monotonic identity.
 *
 * Non-ACCEPTED states (PENDING/DECLINED/…) are ignored — matching the roster
 * precedent at client/tournaments/domain/data-mappers/players.data-mapper.ts. That mapper
 * ALSO filters `!user.isReal`; deliberately not replicated here. `isReal`
 * separates stub player records from real platform users, a roster distinction —
 * our population is defined by the stats lines, and applications are only an
 * enrichment lookup, so filtering them by `isReal` would strip the team off any
 * real-user scorer.
 */
function pickApplications(
  apps: readonly ChampionshipApplicationDto[] | undefined | null,
): Map<number, ChampionshipApplicationDto> {
  const out = new Map<number, ChampionshipApplicationDto>();
  for (const app of apps ?? []) {
    if (app.state !== ACCEPTED_STATE) continue;
    const prev = out.get(app.user.id);
    if (!prev || app.id < prev.id) out.set(app.user.id, app);
  }
  return out;
}

function toResolverTeam(team: StatsTeamDto): ResolverTeam {
  return {
    id: team.id,
    name: team.name,
    shortName: team.shortName,
    logoUrl: team.emblem?.externalUrl,
  };
}

/** Surname-first, matching client/tournaments/domain/data-mappers/players.data-mapper.ts.
 *  Re-implemented rather than imported — cross-module domain imports are avoided
 *  by convention.
 *
 *  This stays the player's `name` (the fallback for placeholders with no
 *  `nameFormat`); the parts travel alongside it on `nameParts`, because which
 *  layout to render is a per-placeholder authoring choice the resolver makes,
 *  not something this boundary can decide once for the whole render. */
function formatPlayerName(user: StatsUserDto): string {
  return [user.surname, user.name, user.middleName].filter(Boolean).join(' ');
}

export const { useGetPlayerStatsRenderContextQuery } = statRenderApi;
